import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Eye, Mail, Clock, AlertTriangle, XCircle, CheckCircle, Gift } from "lucide-react";

interface EmailPreviewDialogProps {
  storeName?: string;
  brandColor?: string;
}

const formatINR = (amount: number): string => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

// NOTE: Using function declaration instead of const arrow to prevent TDZ errors in production builds
export function EmailPreviewDialog({ storeName = "Your Store", brandColor = "#8B5CF6" }: EmailPreviewDialogProps) {
  const sampleData = {
    participantName: "John Doe",
    productName: "Premium Headphones XM5",
    productImage: "https://images.unsplash.com/photo-1505740420928-5e560c06d30e?w=400&h=300&fit=crop",
    productDescription: "Experience crystal-clear audio with our premium wireless headphones featuring active noise cancellation and 30-hour battery life.",
    splitAmount: 2500,
    deadline: new Date(Date.now() + 48 * 60 * 60 * 1000), // 48 hours from now
    organizerName: "Jane Smith",
  };

  const formattedDeadline = sampleData.deadline.toLocaleDateString('en-IN', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

  // Immediate/Standard Reminder Email
  const immediateReminderHtml = `
    <div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; background-color: #f5f5f5; padding: 20px;">
      <div style="max-width: 500px; margin: 0 auto;">
        <div style="background-color: white; border-radius: 16px; padding: 32px; box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);">
          <div style="text-align: center; margin-bottom: 24px;">
            <div style="display: inline-block; width: 50px; height: 50px; background: linear-gradient(135deg, ${brandColor}, #6366F1); border-radius: 10px; line-height: 50px;">
              <span style="color: white; font-size: 22px; font-weight: bold;">S</span>
            </div>
            <h2 style="margin: 12px 0 6px; color: #1a1a1a; font-size: 20px;">You're Invited to Split!</h2>
          </div>
          
          <div style="background-color: #F0FDF4; border: 2px solid #22C55E; border-radius: 10px; padding: 16px; margin-bottom: 20px; text-align: center;">
            <p style="color: #15803D; font-size: 15px; font-weight: 600; margin: 0;">
              🎁 ${sampleData.organizerName} invited you to split a gift!
            </p>
          </div>
          
          <p style="color: #4a4a4a; font-size: 14px; line-height: 1.5; margin-bottom: 16px;">
            Hi ${sampleData.participantName},
          </p>
          
          <p style="color: #4a4a4a; font-size: 14px; line-height: 1.5; margin-bottom: 16px;">
            You've been invited to contribute towards <strong>${sampleData.productName}</strong>.
          </p>
          
          <div style="text-align: center; margin-bottom: 20px;">
            <img src="${sampleData.productImage}" alt="${sampleData.productName}" style="max-width: 100%; height: auto; border-radius: 12px; max-height: 180px; object-fit: cover;" />
          </div>
          
          <p style="color: #6b7280; font-size: 13px; line-height: 1.5; margin-bottom: 20px; font-style: italic;">
            ${sampleData.productDescription}
          </p>
          
          <div style="background-color: #f8f9fa; border-radius: 10px; padding: 20px; margin-bottom: 20px;">
            <div style="border-bottom: 1px solid #e5e7eb; padding-bottom: 12px; margin-bottom: 12px;">
              <div style="display: flex; justify-content: space-between;">
                <span style="color: ${brandColor}; font-size: 14px; font-weight: 600;">Your Share</span>
                <span style="color: ${brandColor}; font-size: 20px; font-weight: bold;">${formatINR(sampleData.splitAmount)}</span>
              </div>
            </div>
            <div style="display: flex; justify-content: space-between;">
              <span style="color: #6b7280; font-size: 13px;">Deadline</span>
              <span style="color: #1a1a1a; font-weight: 500; font-size: 13px;">${formattedDeadline}</span>
            </div>
          </div>
          
          <div style="text-align: center; margin: 24px 0;">
            <span style="display: inline-block; background: linear-gradient(135deg, ${brandColor}, #6366F1); color: white; padding: 14px 32px; border-radius: 8px; font-weight: 600; font-size: 14px;">
              Pay Now - ${formatINR(sampleData.splitAmount)}
            </span>
          </div>
          
          <div style="text-align: center; margin-bottom: 24px;">
            <span style="display: inline-block; background-color: #f3f4f6; color: #4b5563; padding: 10px 24px; border-radius: 6px; font-size: 13px; text-decoration: none;">
              📦 Track Order Progress
            </span>
          </div>
          
          <p style="color: #9ca3af; font-size: 12px; text-align: center; margin-top: 24px;">
            This invitation was sent via Shakatah Split Pay.
          </p>
        </div>
      </div>
    </div>
  `;

  // Urgent (1 hour) Reminder Email
  const urgentReminderHtml = `
    <div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; background-color: #f5f5f5; padding: 20px;">
      <div style="max-width: 500px; margin: 0 auto;">
        <div style="background-color: white; border-radius: 16px; padding: 32px; box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);">
          <div style="text-align: center; margin-bottom: 24px;">
            <div style="display: inline-block; width: 50px; height: 50px; background: linear-gradient(135deg, ${brandColor}, #6366F1); border-radius: 10px; line-height: 50px;">
              <span style="color: white; font-size: 22px; font-weight: bold;">S</span>
            </div>
            <h2 style="margin: 12px 0 6px; color: #1a1a1a; font-size: 20px;">Payment Reminder</h2>
          </div>
          
          <div style="background-color: #FEF2F2; border: 2px solid #EF4444; border-radius: 10px; padding: 16px; margin-bottom: 20px; text-align: center;">
            <p style="color: #DC2626; font-size: 16px; font-weight: bold; margin: 0;">
              ⏰ Less than 1 hour remaining!
            </p>
            <p style="color: #DC2626; font-size: 13px; margin: 6px 0 0;">
              Your payment deadline is approaching!
            </p>
          </div>
          
          <p style="color: #4a4a4a; font-size: 14px; line-height: 1.5; margin-bottom: 16px;">
            Hi ${sampleData.participantName},
          </p>
          
          <p style="color: #4a4a4a; font-size: 14px; line-height: 1.5; margin-bottom: 16px;">
            This is a reminder that your payment for <strong>${sampleData.productName}</strong> is still pending.
          </p>
          
          <div style="text-align: center; margin-bottom: 20px;">
            <img src="${sampleData.productImage}" alt="${sampleData.productName}" style="max-width: 100%; height: auto; border-radius: 12px; max-height: 150px; object-fit: cover;" />
          </div>
          
          <p style="color: #6b7280; font-size: 13px; line-height: 1.5; margin-bottom: 16px; font-style: italic;">
            ${sampleData.productDescription}
          </p>
          
          <div style="background-color: #f8f9fa; border-radius: 10px; padding: 20px; margin-bottom: 20px;">
            <div style="border-bottom: 1px solid #e5e7eb; padding-bottom: 12px; margin-bottom: 12px;">
              <div style="display: flex; justify-content: space-between;">
                <span style="color: ${brandColor}; font-size: 14px; font-weight: 600;">Amount Due</span>
                <span style="color: ${brandColor}; font-size: 20px; font-weight: bold;">${formatINR(sampleData.splitAmount)}</span>
              </div>
            </div>
            <div style="display: flex; justify-content: space-between;">
              <span style="color: #6b7280; font-size: 13px;">Deadline</span>
              <span style="color: #EF4444; font-weight: 600; font-size: 13px;">In less than 1 hour</span>
            </div>
          </div>
          
          <div style="text-align: center; margin: 24px 0;">
            <span style="display: inline-block; background: linear-gradient(135deg, ${brandColor}, #6366F1); color: white; padding: 14px 32px; border-radius: 8px; font-weight: 600; font-size: 14px;">
              Pay Now - ${formatINR(sampleData.splitAmount)}
            </span>
          </div>
          
          <div style="text-align: center; margin-bottom: 24px;">
            <span style="display: inline-block; background-color: #f3f4f6; color: #4b5563; padding: 10px 24px; border-radius: 6px; font-size: 13px; text-decoration: none;">
              📦 Track Order Progress
            </span>
          </div>
          
          <p style="color: #9ca3af; font-size: 12px; text-align: center; margin-top: 24px;">
            This reminder was sent via Shakatah Split Pay.
          </p>
        </div>
      </div>
    </div>
  `;

  // Order Cancelled Email (Organizer)
  const orderCancelledHtml = `
    <div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; background-color: #f5f5f5; padding: 20px;">
      <div style="max-width: 500px; margin: 0 auto;">
        <div style="background-color: white; border-radius: 16px; padding: 32px; box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);">
          <div style="text-align: center; margin-bottom: 24px;">
            <div style="display: inline-block; width: 50px; height: 50px; background: linear-gradient(135deg, ${brandColor}, #6366F1); border-radius: 10px; line-height: 50px;">
              <span style="color: white; font-size: 22px; font-weight: bold;">S</span>
            </div>
            <h2 style="margin: 12px 0 6px; color: #1a1a1a; font-size: 20px;">Order Not Completed</h2>
          </div>
          
          <div style="background-color: #FEF3C7; border: 2px solid #F59E0B; border-radius: 10px; padding: 16px; margin-bottom: 20px; text-align: center;">
            <p style="color: #B45309; font-size: 15px; font-weight: 600; margin: 0;">
              ⚠️ Payment deadline has passed
            </p>
          </div>
          
          <p style="color: #4a4a4a; font-size: 14px; line-height: 1.5; margin-bottom: 16px;">
            Hi ${sampleData.organizerName},
          </p>
          
          <p style="color: #4a4a4a; font-size: 14px; line-height: 1.5; margin-bottom: 16px;">
            Unfortunately, the split payment for <strong>${sampleData.productName}</strong> could not be completed as not all participants paid before the deadline.
          </p>
          
          <div style="text-align: center; margin-bottom: 20px;">
            <img src="${sampleData.productImage}" alt="${sampleData.productName}" style="max-width: 100%; height: auto; border-radius: 12px; max-height: 150px; object-fit: cover;" />
          </div>
          
          <div style="background-color: #f8f9fa; border-radius: 10px; padding: 20px; margin-bottom: 20px;">
            <div style="margin-bottom: 12px;">
              <span style="color: #6b7280; font-size: 13px;">Payment Status</span>
            </div>
            <div style="border-bottom: 1px solid #e5e7eb; padding-bottom: 10px; margin-bottom: 10px;">
              <div style="display: flex; justify-content: space-between;">
                <span style="color: #22C55E; font-size: 13px;">✓ Paid</span>
                <span style="color: #22C55E; font-weight: 600;">2 of 3 participants</span>
              </div>
            </div>
            <div>
              <div style="display: flex; justify-content: space-between;">
                <span style="color: #EF4444; font-size: 13px;">✗ Unpaid</span>
                <span style="color: #EF4444; font-weight: 600;">1 participant</span>
              </div>
            </div>
          </div>
          
          <p style="color: #4a4a4a; font-size: 14px; line-height: 1.5; margin-bottom: 16px;">
            <strong>What happens next:</strong>
          </p>
          <ul style="color: #4a4a4a; font-size: 14px; line-height: 1.8; padding-left: 20px; margin-bottom: 16px;">
            <li>Participants who paid will receive refunds</li>
            <li>Reserved inventory has been released</li>
            <li>You can start a new split payment anytime</li>
          </ul>
          
          <div style="text-align: center; margin-bottom: 24px;">
            <span style="display: inline-block; background-color: #f3f4f6; color: #4b5563; padding: 10px 24px; border-radius: 6px; font-size: 13px; text-decoration: none;">
              📦 View Order Details
            </span>
          </div>
          
          <p style="color: #9ca3af; font-size: 12px; text-align: center; margin-top: 24px;">
            This notification was sent via Shakatah Split Pay.
          </p>
        </div>
      </div>
    </div>
  `;

  // Refund Pending Email (Participant)
  const refundPendingHtml = `
    <div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; background-color: #f5f5f5; padding: 20px;">
      <div style="max-width: 500px; margin: 0 auto;">
        <div style="background-color: white; border-radius: 16px; padding: 32px; box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);">
          <div style="text-align: center; margin-bottom: 24px;">
            <div style="display: inline-block; width: 50px; height: 50px; background: linear-gradient(135deg, ${brandColor}, #6366F1); border-radius: 10px; line-height: 50px;">
              <span style="color: white; font-size: 22px; font-weight: bold;">S</span>
            </div>
            <h2 style="margin: 12px 0 6px; color: #1a1a1a; font-size: 20px;">Refund Pending</h2>
          </div>
          
          <div style="background-color: #FEF3C7; border: 2px solid #F59E0B; border-radius: 10px; padding: 16px; margin-bottom: 20px; text-align: center;">
            <p style="color: #B45309; font-size: 15px; font-weight: 600; margin: 0;">
              💰 Your refund is being processed by the merchant
            </p>
          </div>
          
          <p style="color: #4a4a4a; font-size: 14px; line-height: 1.5; margin-bottom: 16px;">
            Hi ${sampleData.participantName},
          </p>
          
          <p style="color: #4a4a4a; font-size: 14px; line-height: 1.5; margin-bottom: 16px;">
            Unfortunately, the split payment for <strong>${sampleData.productName}</strong> could not be completed as not all participants paid before the deadline.
          </p>
          
          <div style="text-align: center; margin-bottom: 20px;">
            <img src="${sampleData.productImage}" alt="${sampleData.productName}" style="max-width: 100%; height: auto; border-radius: 12px; max-height: 150px; object-fit: cover;" />
          </div>
          
          <p style="color: #4a4a4a; font-size: 14px; line-height: 1.5; margin-bottom: 16px;">
            The merchant has been notified and will process your refund. The amount will be credited back to your original payment method within <strong>5-10 business days</strong>.
          </p>
          
          <div style="background-color: #f8f9fa; border-radius: 10px; padding: 20px; margin-bottom: 20px;">
            <div style="display: flex; justify-content: space-between;">
              <span style="color: #22C55E; font-size: 14px; font-weight: 600;">Refund Amount</span>
              <span style="color: #22C55E; font-size: 20px; font-weight: bold;">${formatINR(sampleData.splitAmount)}</span>
            </div>
          </div>
          
          <p style="color: #6b7280; font-size: 13px; line-height: 1.5;">
            Your gift voucher for this payment has been cancelled. If you have any questions about your refund, please contact the store directly.
          </p>
          
          <p style="color: #9ca3af; font-size: 12px; text-align: center; margin-top: 24px;">
            This notification was sent via Shakatah Split Pay.
          </p>
        </div>
      </div>
    </div>
  `;

  // Payment Confirmed Email (Participant)
  const paymentConfirmedHtml = `
    <div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; background-color: #f5f5f5; padding: 20px;">
      <div style="max-width: 500px; margin: 0 auto;">
        <div style="background-color: white; border-radius: 16px; padding: 32px; box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);">
          
          <div style="text-align: center; margin-bottom: 24px;">
            <div style="display: inline-block; width: 60px; height: 60px; background: linear-gradient(135deg, #22C55E, #16A34A); border-radius: 50%; line-height: 60px;">
              <span style="color: white; font-size: 32px;">✓</span>
            </div>
            <h2 style="margin: 12px 0 6px; color: #1a1a1a; font-size: 22px;">Payment Confirmed!</h2>
            <p style="color: #22C55E; font-weight: 600; margin: 0; font-size: 15px;">Thank you, ${sampleData.participantName}!</p>
          </div>
          
          <div style="background: linear-gradient(135deg, #fdf2f8, #fce7f3); border: 2px solid #f9a8d4; border-radius: 10px; padding: 16px; margin-bottom: 20px; text-align: center;">
            <span style="font-size: 28px;">🎁</span>
            <p style="color: #be185d; font-size: 15px; font-weight: 600; margin: 8px 0 4px;">
              You're part of a special gift!
            </p>
            <p style="color: #9d174d; font-size: 13px; margin: 0;">
              Your contribution is helping make someone's day extra special.
            </p>
          </div>
          
          <div style="text-align: center; margin-bottom: 20px;">
            <img src="${sampleData.productImage}" alt="${sampleData.productName}" style="max-width: 100%; height: auto; border-radius: 12px; max-height: 150px; object-fit: cover;" />
          </div>
          
          <h3 style="color: #1a1a1a; font-size: 16px; margin: 0 0 8px; text-align: center;">${sampleData.productName}</h3>
          <p style="color: #6b7280; font-size: 13px; line-height: 1.5; margin: 0 0 20px; font-style: italic; text-align: center;">
            ${sampleData.productDescription}
          </p>
          
          <div style="background-color: #f8f9fa; border-radius: 10px; padding: 16px; margin-bottom: 20px;">
            <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px; border-bottom: 1px solid #e5e7eb; padding-bottom: 10px;">
              <span style="color: #6b7280; font-size: 11px;">VOUCHER NUMBER</span>
              <span style="color: ${brandColor}; font-weight: 600; font-size: 12px; font-family: monospace;">GV-2024-001234</span>
            </div>
            <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px;">
              <span style="color: #6b7280; font-size: 13px;">Base Amount</span>
              <span style="color: #1a1a1a;">₹2,118.64</span>
            </div>
            <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px;">
              <span style="color: #6b7280; font-size: 13px;">GST (18%)</span>
              <span style="color: #1a1a1a;">₹381.36</span>
            </div>
            <div style="display: flex; justify-content: space-between; align-items: center; border-top: 2px solid #e5e7eb; padding-top: 12px; margin-top: 8px;">
              <span style="color: #1a1a1a; font-weight: 600; font-size: 14px;">Total Paid</span>
              <span style="color: #22C55E; font-weight: bold; font-size: 20px;">${formatINR(sampleData.splitAmount)}</span>
            </div>
          </div>
          
          <div style="background: #F0FDF4; border: 1px solid #86EFAC; border-radius: 8px; padding: 10px; margin-bottom: 20px;">
            <p style="color: #6b7280; font-size: 11px; margin: 0 0 4px;">Payment Reference</p>
            <p style="color: #166534; font-size: 11px; font-family: monospace; margin: 0;">pay_O1234567890ABCD</p>
          </div>
          
          <div style="text-align: center; margin: 24px 0;">
            <span style="display: inline-block; background: linear-gradient(135deg, ${brandColor}, #6366F1); color: white; padding: 14px 32px; border-radius: 8px; font-weight: 600; font-size: 14px;">
              📦 Track Order Progress
            </span>
          </div>
          
          <div style="background: #FEF3C7; border-radius: 8px; padding: 14px; margin-bottom: 20px;">
            <p style="color: #92400E; font-weight: 600; margin: 0 0 6px; font-size: 13px;">⏳ What happens next?</p>
            <p style="color: #B45309; font-size: 12px; margin: 0; line-height: 1.5;">
              Once all participants pay, your gift voucher will be redeemed and the order will be fulfilled!
            </p>
          </div>
          
          <p style="color: #9ca3af; font-size: 11px; text-align: center; margin-top: 20px;">
            Keep this email for your records. Powered by Shakatah Split Payments.
          </p>
        </div>
      </div>
    </div>
  `;

  // Order Complete Email (All Participants + Organizer)
  const orderCompleteHtml = `
    <div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; background-color: #f5f5f5; padding: 20px;">
      <div style="max-width: 500px; margin: 0 auto;">
        <div style="background-color: white; border-radius: 16px; padding: 32px; box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);">
          
          <div style="text-align: center; margin-bottom: 24px;">
            <div style="font-size: 50px; margin-bottom: 12px;">🎁</div>
            <h2 style="margin: 0 0 6px; color: #1a1a1a; font-size: 22px;">Gift Order Complete!</h2>
            <p style="color: #22C55E; font-weight: 600; margin: 0; font-size: 15px;">
              Thanks for contributing, ${sampleData.participantName}!
            </p>
          </div>
          
          <div style="background: linear-gradient(135deg, #fdf2f8, #fce7f3); border: 2px solid #f9a8d4; border-radius: 10px; padding: 16px; margin-bottom: 20px; text-align: center;">
            <p style="color: #be185d; font-size: 15px; font-weight: 600; margin: 0;">
              🎁 The group gift you contributed to is on its way!
            </p>
          </div>
          
          <p style="color: #4a4a4a; font-size: 14px; line-height: 1.5; margin-bottom: 16px;">
            All participants have paid for <strong>${sampleData.productName}</strong>. The order has been placed and is being processed!
          </p>
          
          <div style="text-align: center; margin-bottom: 20px;">
            <img src="${sampleData.productImage}" alt="${sampleData.productName}" style="max-width: 100%; height: auto; border-radius: 12px; max-height: 150px; object-fit: cover;" />
          </div>
          
          <p style="color: #6b7280; font-size: 13px; line-height: 1.5; margin: 0 0 20px; font-style: italic; text-align: center;">
            ${sampleData.productDescription}
          </p>
          
          <div style="background-color: #ECFDF5; border: 1px solid #10B981; border-radius: 10px; padding: 16px; margin-bottom: 20px;">
            <h3 style="color: #065F46; margin: 0 0 12px; font-size: 14px;">✅ Payment Complete</h3>
            <div style="display: flex; justify-content: space-between; margin-bottom: 8px;">
              <span style="color: #065F46; font-size: 13px;">Total Amount</span>
              <span style="color: #065F46; font-weight: 600;">₹10,000.00</span>
            </div>
            <div style="display: flex; justify-content: space-between;">
              <span style="color: #065F46; font-size: 13px;">Participants</span>
              <span style="color: #065F46; font-weight: 600;">4 paid ✓</span>
            </div>
          </div>
          
          <div style="background: #EFF6FF; border: 1px solid #3B82F6; border-radius: 8px; padding: 14px; margin-bottom: 20px;">
            <p style="color: #1E40AF; font-weight: 600; margin: 0 0 6px; font-size: 13px;">📦 What happens next?</p>
            <p style="color: #1E3A8A; font-size: 12px; margin: 0; line-height: 1.5;">
              The order is being prepared. You'll receive updates when it ships and is delivered.
            </p>
          </div>
          
          <div style="text-align: center; margin: 24px 0;">
            <span style="display: inline-block; background: linear-gradient(135deg, #10B981, #059669); color: white; padding: 14px 32px; border-radius: 8px; font-weight: 600; font-size: 14px;">
              📦 Track Order Progress
            </span>
          </div>
          
          <p style="color: #9ca3af; font-size: 11px; text-align: center; margin-top: 20px;">
            Thank you for using Shakatah Split Payments!
          </p>
        </div>
      </div>
    </div>
  `;

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2">
          <Eye className="h-4 w-4" />
          Preview Emails
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Email Preview
          </DialogTitle>
          <DialogDescription>
            Preview how reminder emails will appear to participants and organizers
          </DialogDescription>
        </DialogHeader>
        
        <Tabs defaultValue="immediate" className="flex-1 overflow-hidden flex flex-col">
          <TabsList className="grid w-full grid-cols-6">
            <TabsTrigger value="immediate" className="text-xs gap-1 px-2">
              <Mail className="h-3 w-3" />
              <span className="hidden sm:inline">Invite</span>
            </TabsTrigger>
            <TabsTrigger value="confirmed" className="text-xs gap-1 px-2">
              <CheckCircle className="h-3 w-3" />
              <span className="hidden sm:inline">Paid</span>
            </TabsTrigger>
            <TabsTrigger value="complete" className="text-xs gap-1 px-2">
              <Gift className="h-3 w-3" />
              <span className="hidden sm:inline">Complete</span>
            </TabsTrigger>
            <TabsTrigger value="urgent" className="text-xs gap-1 px-2">
              <Clock className="h-3 w-3" />
              <span className="hidden sm:inline">Urgent</span>
            </TabsTrigger>
            <TabsTrigger value="cancelled" className="text-xs gap-1 px-2">
              <XCircle className="h-3 w-3" />
              <span className="hidden sm:inline">Expired</span>
            </TabsTrigger>
            <TabsTrigger value="refund" className="text-xs gap-1 px-2">
              <AlertTriangle className="h-3 w-3" />
              <span className="hidden sm:inline">Refund</span>
            </TabsTrigger>
          </TabsList>
          
          <div className="flex-1 overflow-auto mt-4 border rounded-lg bg-gray-100">
            <TabsContent value="immediate" className="m-0 h-full">
              <div dangerouslySetInnerHTML={{ __html: immediateReminderHtml }} />
            </TabsContent>
            <TabsContent value="confirmed" className="m-0 h-full">
              <div dangerouslySetInnerHTML={{ __html: paymentConfirmedHtml }} />
            </TabsContent>
            <TabsContent value="complete" className="m-0 h-full">
              <div dangerouslySetInnerHTML={{ __html: orderCompleteHtml }} />
            </TabsContent>
            <TabsContent value="urgent" className="m-0 h-full">
              <div dangerouslySetInnerHTML={{ __html: urgentReminderHtml }} />
            </TabsContent>
            <TabsContent value="cancelled" className="m-0 h-full">
              <div dangerouslySetInnerHTML={{ __html: orderCancelledHtml }} />
            </TabsContent>
            <TabsContent value="refund" className="m-0 h-full">
              <div dangerouslySetInnerHTML={{ __html: refundPendingHtml }} />
            </TabsContent>
          </div>
        </Tabs>
        
        <p className="text-xs text-muted-foreground text-center pt-2">
          Sample data shown. Actual emails will use real order details.
        </p>
      </DialogContent>
    </Dialog>
  );
};
